import { HelpInfoModal, HelpIconButton } from './shared/ApplicantWizardModals';
import { FIELD_HELP_COPY } from '../lib/stepHelpCopy';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../lib/firebase';
import { useModalLayer } from '../lib/modalStack';
import { Program } from '../types';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  Upload, CheckCircle, ChevronLeft, ChevronRight, X, Check, GraduationCap,
  CalendarDays, FileText, Shield, Sparkles, ArrowRight, Loader, AlertCircle, WifiOff, Tag,
} from 'lucide-react';

interface WizardData {
  programId: string;
  programName: string;
  pricingVariableId?: string;
  term: string;
  requirementsConfirmed: boolean;
  resumeUploaded: boolean;
  resumeParsed: boolean;
  parsedFirstName?: string;
  parsedLastName?: string;
  parsedEmail?: string;
  parsedPhone?: string;
  copyFromAppId?: string;
}

interface ApplicationWizardProps {
  programsList: Program[];
  /** Previous applications available to copy details from for easy reapplication */
  previousApplications?: any[];
  /** When true, Step 1 shows a loading skeleton instead of the empty-state message. */
  programsLoading?: boolean;
  onComplete: (data: WizardData) => Promise<void>;
  onClose: () => void;
  mode: 'admin' | 'applicant';
  applicantName?: string;
  applicantEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  /** When set and not 'approved', shows a banner at the top of Step 1 */
  verificationStatus?: string;
}


/* ─── §3.4 wizard progress persistence ───
 * Closing the wizard used to reset it to Step 1 (the audit hit this
 * three times in one session). Progress is now persisted per account +
 * mode and restored on reopen, so an interrupted applicant resumes at
 * the step they left. The File object itself cannot persist — the
 * parsed results and the file name do, which is everything Step 4
 * displays. Cleared on successful creation and after 7 days. */
const WIZARD_PROGRESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface WizardProgress {
  uid: string;
  mode: string;
  step: number;
  programId: string;
  programName: string;
  pricingVariableId?: string;
  term: string;
  checkboxEligibility: boolean;
  checkboxReadUnderstand: boolean;
  copyFromAppId: string;
  resumeUploaded: boolean;
  resumeParsed: boolean;
  resumeFileName: string;
  parsedFirstName: string;
  parsedLastName: string;
  parsedEmail: string;
  parsedPhone: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  ts: number;
}

function wizardProgressKey(uid: string, mode: string): string {
  return `applicationWizardProgress:${uid}:${mode}`;
}

function loadWizardProgress(mode: string): WizardProgress | null {
  try {
    const uid = getAuth().currentUser?.uid || '';
    if (!uid) return null;
    const raw = localStorage.getItem(wizardProgressKey(uid, mode));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WizardProgress;
    if (!parsed || parsed.uid !== uid || parsed.mode !== mode) return null;
    if (Date.now() - Number(parsed.ts || 0) > WIZARD_PROGRESS_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearWizardProgress(mode: string): void {
  try {
    const uid = getAuth().currentUser?.uid || '';
    if (uid) localStorage.removeItem(wizardProgressKey(uid, mode));
  } catch {
    /* storage unavailable */
  }
}


/* ─── Step transition wrapper ─── */
function StepSlide({ step, children }: { step: number; children: React.ReactNode }) {
  return (
    <div
      key={step}
      style={{
        animation: 'wizFadeIn 0.25s ease-out, wizSlideUp 0.3s ease-out',
      }}
    >
      <style>{`@keyframes wizFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes wizSlideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {children}
    </div>
  );
}

/* ─── Card button component ─── */
function CardButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer active:scale-[0.98] ${
        selected
          ? 'border-brand bg-blue-50/80 ring-2 ring-blue-200 shadow-sm shadow-blue-100'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 active:bg-slate-100'
      }`}
    >
      {children}
    </button>
  );
}

export default function ApplicationWizard({
  programsList,
  previousApplications = [],
  programsLoading = false,
  onComplete,
  onClose,
  mode,
  applicantName,
  applicantEmail,
  initialFirstName,
  initialLastName,
  initialEmail,
  verificationStatus,
}: ApplicationWizardProps) {
  const zIndex = useModalLayer();
  // §3.4: restore any in-progress wizard state for this account + mode.
  const [initialProgress] = useState<WizardProgress | null>(() => loadWizardProgress(mode));
  const restoredRef = useRef(!!initialProgress);
  const [currentStep, setCurrentStep] = useState(initialProgress?.step ?? 1);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [selectedCopyAppId, setSelectedCopyAppId] = useState<string>(initialProgress?.copyFromAppId ?? '');

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Prompt user before unloading/refreshing if application wizard is in progress
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentStep > 1 && currentStep < 6) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep]);
  const [selectedProgramId, setSelectedProgramId] = useState(initialProgress?.programId ?? '');
  const [selectedProgramName, setSelectedProgramName] = useState(initialProgress?.programName ?? '');
  const [selectedPricingVariableId, setSelectedPricingVariableId] = useState<string>(initialProgress?.pricingVariableId ?? '');
  const [selectedTerm, setSelectedTerm] = useState(initialProgress?.term ?? '');
  // Admin-entered applicant details (used only in admin mode)
  const [adminFirstName, setAdminFirstName] = useState(initialFirstName || initialProgress?.adminFirstName || '');
  const [adminLastName, setAdminLastName] = useState(initialLastName || initialProgress?.adminLastName || '');
  const [adminEmail, setAdminEmail] = useState(initialEmail || initialProgress?.adminEmail || '');
  const [requirementsText, setRequirementsText] = useState<string | null>(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  // Firestore-fetched terms — the source of truth, independent of programsList prop
  const [firestoreTerms, setFirestoreTerms] = useState<string[] | null>(null);
  const [checkboxEligibility, setCheckboxEligibility] = useState(initialProgress?.checkboxEligibility ?? false);
  const [checkboxReadUnderstand, setCheckboxReadUnderstand] = useState(initialProgress?.checkboxReadUnderstand ?? false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeFileName, setResumeFileName] = useState(initialProgress?.resumeFileName ?? '');
  const [isParsing, setIsParsing] = useState(false);
  const [resumeUploaded, setResumeUploaded] = useState(initialProgress?.resumeUploaded ?? false);
  const [resumeInteractionDone, setResumeInteractionDone] = useState(
    (initialProgress?.resumeUploaded ?? false) || (initialProgress?.resumeParsed ?? false),
  );
  const [resumeError, setResumeError] = useState('');
  const [resumeParsed, setResumeParsed] = useState(initialProgress?.resumeParsed ?? false);
  const [parsedFirstName, setParsedFirstName] = useState(initialProgress?.parsedFirstName ?? '');
  const [parsedLastName, setParsedLastName] = useState(initialProgress?.parsedLastName ?? '');
  const [parsedEmail, setParsedEmail] = useState(initialProgress?.parsedEmail ?? '');
  const [parsedPhone, setParsedPhone] = useState(initialProgress?.parsedPhone ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [wizardHelpModal, setWizardHelpModal] = useState<{ title: string; explanation: string; isSensitive?: boolean } | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);

  const [programSearch, setProgramSearch] = useState('');

  const activePrograms = programsList.filter(
    (p) => p.active !== false && (p.terms || p.intakes) && (p.terms || p.intakes).some((t: any) => t.isOpen !== false)
  ).filter((p) => {
    if (!programSearch.trim()) return true;
    const q = programSearch.trim().toLowerCase();
    const codeStr = String(p.code || '').toLowerCase();
    const shortCodeStr = String(p.shortCode || '').toLowerCase();
    const progCodeStr = String(p.programCode || '').toLowerCase();
    const nameStr = String(p.name || p.title || '').toLowerCase();
    const catStr = String(p.category || p.type || '').toLowerCase();
    const idStr = String(p.id || p.programId || '').toLowerCase();
    return codeStr.includes(q) || shortCodeStr.includes(q) || progCodeStr.includes(q) || nameStr.includes(q) || catStr.includes(q) || idStr.includes(q);
  });

  const selectedProgram = activePrograms.find(
    (p) => p.id === selectedProgramId || p.programId === selectedProgramId
  );

  // Stable derived key for the selected program — avoids optional chaining in deps
  const progId = selectedProgram?.id || selectedProgramId;

  // Load requirements & terms from Firestore when program changes
  useEffect(() => {
    if (!progId) {
      setRequirementsText(null);
      setFirestoreTerms(null);
      setRequirementsLoading(false);
      // §3.4: on a restored session the saved checkbox answers survive —
      // the reset below is for genuine program changes only.
      if (!restoredRef.current) {
        setCheckboxEligibility(false);
        setCheckboxReadUnderstand(false);
      }
      return;
    }
    setRequirementsText(null);
    setFirestoreTerms(null);
    setRequirementsLoading(true);
    if (!restoredRef.current) {
      setCheckboxEligibility(false);
      setCheckboxReadUnderstand(false);
    } else {
      restoredRef.current = false;
    }

    getDoc(doc(db, 'programs', progId))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setRequirementsText(data.requirements || '');
          const rawTermsList = data.intakes || data.terms;
          setFirestoreTerms(Array.isArray(rawTermsList) ? rawTermsList : null);
        } else {
          setRequirementsText('');
          setFirestoreTerms(null);
        }
      })
      .catch(() => {
        setRequirementsText('');
        setFirestoreTerms(null);
      })
      .finally(() => setRequirementsLoading(false));
  }, [progId]); // single stable dependency — no optional chaining

  // When the wizard mounts, make the background content inert so the search
  // input and other interactive elements behind the modal cannot receive focus.
  useEffect(() => {
    // Find the main content wrapper (sibling of the portal target) and add inert
    const main = document.querySelector('main');
    const prev = main?.getAttribute('inert') || '';
    main?.setAttribute('inert', '');
    return () => {
      if (prev === '') main?.removeAttribute('inert');
      else main?.setAttribute('inert', prev);
    };
  }, []);

  // Reset term when program changes (but not on mount: a restored
  // session's saved term must survive its own program value — §3.4).
  const programMountRef = useRef(true);
  useEffect(() => {
    if (programMountRef.current) {
      programMountRef.current = false;
      return;
    }
    setSelectedTerm('');
  }, [selectedProgramId]);

  // §3.4: persist progress on every meaningful change so closing and
  // reopening the wizard resumes where the user left off.
  useEffect(() => {
    try {
      const uid = getAuth().currentUser?.uid || '';
      if (!uid) return;
      const progress: WizardProgress = {
        uid,
        mode,
        step: currentStep,
        programId: selectedProgramId,
        programName: selectedProgramName,
        term: selectedTerm,
        checkboxEligibility,
        checkboxReadUnderstand,
        copyFromAppId: selectedCopyAppId,
        resumeUploaded,
        resumeParsed,
        resumeFileName,
        parsedFirstName,
        parsedLastName,
        parsedEmail,
        parsedPhone,
        adminFirstName,
        adminLastName,
        adminEmail,
        ts: Date.now(),
      };
      localStorage.setItem(wizardProgressKey(uid, mode), JSON.stringify(progress));
    } catch {
      /* storage unavailable — wizard still works, just without resume */
    }
  }, [mode, currentStep, selectedProgramId, selectedProgramName, selectedTerm,
    checkboxEligibility, checkboxReadUnderstand, selectedCopyAppId,
    resumeUploaded, resumeParsed, resumeFileName, parsedFirstName,
    parsedLastName, parsedEmail, parsedPhone, adminFirstName, adminLastName,
    adminEmail]);

  // Scroll body to top when step changes
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Derive available visible pricing variables for selected program
  const visiblePricingVariables = useMemo(() => {
    if (!selectedProgram || !Array.isArray(selectedProgram.pricingVariables)) return [];
    return selectedProgram.pricingVariables.filter((v: any) => v.visibility === 'visible');
  }, [selectedProgram]);

  const requiresPricingVariable = visiblePricingVariables.length > 0;

  const stepList = useMemo(() => {
    const steps = [
      { id: 'program', label: 'Program', icon: GraduationCap },
      { id: 'term', label: 'Term', icon: CalendarDays },
    ];
    if (requiresPricingVariable) {
      steps.push({ id: 'variable', label: 'Pricing Option', icon: Tag });
    }
    steps.push({ id: 'requirements', label: 'Requirements', icon: Shield });
    steps.push({ id: 'resume', label: 'Resume (Optional)', icon: FileText });
    return steps;
  }, [requiresPricingVariable]);

  // Adjust currentStep if step count changes when changing program
  useEffect(() => {
    if (currentStep > stepList.length) {
      setCurrentStep(stepList.length);
    }
  }, [stepList.length, currentStep]);

  const canProceedFromStep1 =
    selectedProgramId !== '' &&
    (mode !== 'admin' || (adminFirstName.trim() && adminLastName.trim() && adminEmail.trim()));
  const canProceedFromStep2 = selectedTerm !== '';
  const canProceedFromStep3 = checkboxEligibility && checkboxReadUnderstand;
  const canProceedFromStep4 = resumeUploaded || (resumeInteractionDone && !isParsing);

  const handleResumeUpload = useCallback(async (file: File) => {
    setResumeFile(file);
    setResumeFileName(file.name);
    setIsParsing(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

      const token = await getAuth().currentUser?.getIdToken();
      const res = await fetch('/api/resume/parse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          resumeData: base64,
          fileName: file.name,
          mimeType: file.type,
        }),
      });
      // Safe JSON parse — the server may return a non-JSON 404 body
      let data: any = {};
      try {
        data = await res.json();
      } catch {
        data = { error: `Server error ${res.status}` };
      }
      if (!res.ok) throw new Error(data.error || `Resume parse failed (${res.status})`);

      const hasExtractable = !!(data.parsed?.firstName || data.parsed?.lastName || data.parsed?.email || data.parsed?.phone);
      if (!hasExtractable) {
        throw new Error('We could not extract any usable information from your resume.');
      }

      if (data.parsed?.firstName) {
        setParsedFirstName(data.parsed.firstName);
      }
      if (data.parsed?.lastName) {
        setParsedLastName(data.parsed.lastName);
      }
      if (data.parsed?.email) {
        setParsedEmail(data.parsed.email);
      }
      if (data.parsed?.phone) setParsedPhone(data.parsed.phone);
      // Only overwrite admin-entered fields from parsed data when parsing
      // actually produced a non-empty value for that field. This prevents
      // an empty parsed return from wiping out values the admin typed in Step 1.
      if (mode === 'admin') {
        if (data.parsed?.firstName) setAdminFirstName(data.parsed.firstName);
        if (data.parsed?.lastName) setAdminLastName(data.parsed.lastName);
        if (data.parsed?.email) setAdminEmail(data.parsed.email);
      }
      setResumeParsed(true);
      setResumeInteractionDone(true);
    } catch (err) {
      console.error('Resume parse failed:', err);
      setResumeError(err instanceof Error ? err.message : 'We could not extract data from your resume. You can continue without one or try a different file.');
      setResumeParsed(false);
      setResumeInteractionDone(true);
    } finally {
      setIsParsing(false);
      setResumeUploaded(true);
    }
  }, [mode]);

  const handleSkipResume = () => {
    setResumeUploaded(false);
    setResumeParsed(false);
    setResumeFile(null);
    setResumeFileName('');
    setResumeInteractionDone(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onComplete({
        programId: selectedProgramId,
        programName: selectedProgramName,
        pricingVariableId: selectedPricingVariableId || undefined,
        term: selectedTerm,
        requirementsConfirmed: true,
        resumeUploaded,
        resumeParsed,
        parsedFirstName: parsedFirstName || adminFirstName,
        parsedLastName: parsedLastName || adminLastName,
        parsedEmail: parsedEmail || adminEmail,
        parsedPhone,
        copyFromAppId: selectedCopyAppId || undefined,
      });
      // Creation succeeded — the saved progress has served its purpose.
      clearWizardProgress(mode);
    } catch {
      // Parent handler handles its own errors; swallow here (progress is
      // kept, so a failed creation never loses the user's place).
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── Progress bar (mobile + desktop) ─── */
  const renderProgress = () => (
    <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2">
      {/* Mobile: compact icons + labels */}
      <div className="flex items-center justify-between sm:hidden overflow-x-auto no-scrollbar gap-1 py-1">
        {stepList.map((stepItem, idx) => {
          const stepNum = idx + 1;
          const StepIcon = stepItem.icon;
          const isActive = currentStep === stepNum;
          const isDone = currentStep > stepNum;
          return (
            <React.Fragment key={stepItem.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isDone
                      ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-200'
                      : isActive
                      ? 'btn-primary text-white ring-[3px] ring-blue-100 shadow-sm shadow-blue-200'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isDone ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <StepIcon className="h-4 w-4" />
                  )}
                </div>
                <span
                  className={`text-[11px] font-bold mt-1.5 whitespace-nowrap tracking-tight ${
                    isDone
                      ? 'text-emerald-600'
                      : isActive
                      ? 'text-brand'
                      : 'text-slate-400'
                  }`}
                >
                  {stepItem.label}
                </span>
              </div>
              {idx < stepList.length - 1 && (
                <div
                  className={`flex-1 h-[3px] mx-1.5 rounded-full ${
                    isDone ? 'bg-emerald-400' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Desktop: larger progress */}
      <div className="hidden sm:flex items-center justify-between">
        {stepList.map((stepItem, idx) => {
          const stepNum = idx + 1;
          const StepIcon = stepItem.icon;
          const isActive = currentStep === stepNum;
          const isDone = currentStep > stepNum;
          return (
            <React.Fragment key={stepItem.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                    isActive
                      ? 'btn-primary text-white ring-4 ring-blue-100'
                      : isDone
                      ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-200'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isDone ? (
                    <Check className="h-[18px] w-[18px]" />
                  ) : (
                    <StepIcon className="h-[18px] w-[18px]" />
                  )}
                </div>
                <span
                  className={`text-[11px] font-bold mt-1.5 whitespace-nowrap ${
                    isActive
                      ? 'text-brand'
                      : isDone
                      ? 'text-emerald-600'
                      : 'text-slate-400'
                  }`}
                >
                  {stepItem.label}
                </span>
              </div>
              {idx < stepList.length - 1 && (
                <div
                  className={`flex-1 h-[3px] mx-3 rounded-full ${
                    isDone ? 'bg-emerald-400' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  /* ─── Step 1: Program Selection ─── */
  const renderStep1 = () => (
    <StepSlide step={1}>
      <div className="space-y-4">
        {mode === 'applicant' && verificationStatus && verificationStatus !== 'approved' && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-800">Identity verification required</p>
              <p className="text-[11px] text-amber-700 mt-0.5">
                You will need to complete identity verification before you can apply to any jobs.
              </p>
            </div>
          </div>
        )}
        {mode === 'admin' && (
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-4 sm:p-5 space-y-3 border border-slate-200 shadow-sm">
            <div className="flex items-center space-x-2">
              <div className="h-7 w-7 rounded-lg bg-blue-100 flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-brand" />
              </div>
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                Applicant Details
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between mb-1"><label className="text-xs font-semibold text-slate-500">First Name *</label><HelpIconButton onClick={() => setWizardHelpModal({ title: FIELD_HELP_COPY.firstName.title, explanation: FIELD_HELP_COPY.firstName.explanation })} /></div>
                <input
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  placeholder="John"
                  className="w-full px-3.5 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400 bg-white transition-all"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between mb-1"><label className="text-xs font-semibold text-slate-500">Last Name *</label><HelpIconButton onClick={() => setWizardHelpModal({ title: FIELD_HELP_COPY.lastName.title, explanation: FIELD_HELP_COPY.lastName.explanation })} /></div>
                <input
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  placeholder="Doe"
                  className="w-full px-3.5 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400 bg-white transition-all"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between mb-1"><label className="text-xs font-semibold text-slate-500">Email *</label><HelpIconButton onClick={() => setWizardHelpModal({ title: FIELD_HELP_COPY.email.title, explanation: FIELD_HELP_COPY.email.explanation })} /></div>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="john@example.com"
                className="w-full px-3.5 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400 bg-white transition-all"
                required
              />
            </div>
          </div>
        )}

        <div>
          {mode === 'applicant' && previousApplications && previousApplications.length > 0 && (
            <div className="mb-4 p-3.5 bg-blue-50/70 border border-blue-200/80 rounded-2xl space-y-1.5">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-brand" />
                <span>Pre-fill information from a previous application (Optional)</span>
              </label>
              <p className="text-[11px] text-slate-500">
                Select an existing application to copy your Personal Details, School, Work Experience, and Emergency Contact details into this new application.
              </p>
              <select
                value={selectedCopyAppId}
                onChange={(e) => setSelectedCopyAppId(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              >
                <option value="">Start Fresh (Do not copy details)</option>
                {previousApplications.map((app: any) => (
                  <option key={app.applicationId || app.id} value={app.applicationId || app.id}>
                    Copy from: {app.programType || 'Application'} ({app.applicationId || app.id}) - {app.programTerm || 'Draft'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <p className="text-sm font-semibold text-slate-700 mb-2">
            {mode === 'admin' ? 'Choose a program for this applicant' : 'Choose your program'}
          </p>

          <div className="mb-3">
            <input
              type="text"
              value={programSearch}
              onChange={(e) => setProgramSearch(e.target.value)}
              placeholder="Search by Program Code or Name (e.g. 1001 or Camp Counselor)..."
              className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-blue-400 bg-white transition-all"
            />
          </div>

          {/* Show skeleton cards while programs are loading */}
          {programsLoading && activePrograms.length === 0 ? (
            <div className="space-y-2.5 animate-pulse" aria-label="Loading programs">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 p-4 sm:p-5 rounded-2xl border-2 border-slate-100 bg-slate-50">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-slate-200 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-2/3 bg-slate-200 rounded-lg" />
                    <div className="h-3 w-full bg-slate-100 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2.5 max-h-[40vh] overflow-y-auto pr-1 -mr-1">
              {activePrograms.map((prog) => {
                const progId = prog.id || prog.programId || '';
                const isSelected = selectedProgramId === progId;
                return (
                  <div key={progId}>
                    <CardButton
                      selected={isSelected}
                      onClick={() => {
                        setSelectedProgramId(progId);
                        setSelectedProgramName(prog.name);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-10 w-10 sm:h-12 sm:w-12 rounded-2xl flex items-center justify-center shrink-0 transition-all ${
                            isSelected
                              ? 'bg-blue-100 text-brand'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-bold text-slate-800 truncate">
                            {(() => {
                              const pCode = prog.code || prog.shortCode || prog.programCode;
                              return pCode ? `${prog.name} (${pCode})` : prog.name;
                            })()}
                          </p>
                          {prog.description && (
                            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">
                              {prog.description}
                            </p>
                          )}
                        </div>
                        <div
                          className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${
                            isSelected
                              ? 'btn-primary scale-100 opacity-100'
                              : 'bg-slate-200 scale-90 opacity-0'
                          }`}
                        >
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                      </div>
                    </CardButton>
                    {/* Configuration Required notice: restored during PR #1418 review.
                        The variable-step move dropped the shipped warning shown when a
                        program HAS pricing variables but every one is hidden. */}
                    {isSelected && visiblePricingVariables.length === 0 && Array.isArray(prog.pricingVariables) && prog.pricingVariables.length > 0 && (
                      <div className="mt-2.5 ml-4 sm:ml-6 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                        <strong>Configuration Required:</strong> All pricing options for this program are currently hidden or unavailable. Please contact an administrator to resolve this program configuration before applying.
                      </div>
                    )}
                  </div>
                );
              })}
              {activePrograms.length === 0 && (
                <div className="text-center py-8 px-4 bg-slate-50 rounded-2xl">
                  <p className="text-sm font-bold text-slate-600">No programs available at this time</p>
                  <p className="text-xs text-slate-400 mt-1">No programs are currently open for applications in this workspace. Please check back later, or contact the support team if you believe a program should be listed here.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </StepSlide>
  );

  /* ─── Variable Selection Step ─── */
  const renderVariableStep = () => (
    <StepSlide step={currentStep}>
      <div className="space-y-4">
        <div className="flex items-center space-x-2.5 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3">
          <div className="h-8 w-8 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
            <GraduationCap className="h-4 w-4 text-brand" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Program & Term</p>
            <p className="text-xs font-bold text-blue-900 truncate">
              {selectedProgram?.name || '—'} • <span className="text-brand">{selectedTerm || '—'}</span>
            </p>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-700 mb-1">Select Pricing Option / Variable *</p>
          <p className="text-xs text-slate-500 mb-3">
            Choose the pricing variable for your application under this program.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {visiblePricingVariables.map((v: any) => {
              const isVarSelected = selectedPricingVariableId === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setSelectedPricingVariableId(v.id)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 cursor-pointer active:scale-[0.98] ${
                    isVarSelected
                      ? 'border-brand bg-blue-50/80 ring-2 ring-blue-200 shadow-sm shadow-blue-100'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-slate-800 text-sm">{v.name}</span>
                    {v.code && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 rounded-md text-slate-600 font-semibold shrink-0">
                        Code: {v.code}
                      </span>
                    )}
                  </div>
                  {v.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{v.description}</p>
                  )}
                  <div className="text-emerald-700 font-extrabold text-sm mt-2 flex items-center justify-between">
                    <span>${Number(v.sellingPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {isVarSelected && (
                      <div className="h-5 w-5 rounded-full btn-primary flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </StepSlide>
  );

  /* ─── Step 2: Term Selection ─── */
  const renderStep2 = () => {
    // Prefer Firestore-fetched terms (always fresh), fall back to programsList prop
    const rawTerms = (firestoreTerms && firestoreTerms.length > 0)
      ? firestoreTerms
      : (selectedProgram?.terms || selectedProgram?.intakes || []);

    // Strictly filter to only open terms (where t.isOpen !== false)
    const terms = (rawTerms || []).filter((t: any) => typeof t === 'string' || t.isOpen !== false);
    const hasTerms = terms && terms.length > 0;

    return (
      <StepSlide step={2}>
        <div className="space-y-4">
          <div className="flex items-center space-x-2.5 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3">
            <div className="h-8 w-8 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <GraduationCap className="h-4 w-4 text-brand" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Program</p>
              <p className="text-sm font-bold text-blue-900 truncate">{selectedProgram?.name || '—'}</p>
            </div>
          </div>

          {requirementsLoading && !hasTerms ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 w-24 bg-slate-200 rounded-lg" />
              <div className="grid grid-cols-2 gap-2.5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded-2xl" />
                ))}
              </div>
            </div>
          ) : hasTerms ? (
            <>
              <p className="text-sm font-semibold text-slate-700">Select your term:</p>
              <div className="grid grid-cols-2 gap-2.5">
                {terms?.map((term: any) => {
                  const termLabel = typeof term === 'string' ? term : term.label || term.termId;
                  const termValue = typeof term === 'string' ? term : term.label || term.termId;
                  return (
                    <button
                      key={termValue}
                      type="button"
                      onClick={() => setSelectedTerm(termValue)}
                      className={`p-4 sm:p-5 rounded-2xl border-2 text-center transition-all duration-200 cursor-pointer active:scale-[0.97] ${
                        selectedTerm === termValue
                          ? 'border-brand bg-blue-50/80 ring-2 ring-blue-200 shadow-sm shadow-blue-100'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <p className="text-sm sm:text-base font-bold text-slate-800">{termLabel}</p>
                      {selectedTerm === termValue && (
                        <div className="h-5 w-5 rounded-full btn-primary flex items-center justify-center mx-auto mt-2">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center p-6 bg-amber-50 border border-amber-200 rounded-2xl">
              <p className="text-sm font-semibold text-amber-800">No open terms available</p>
              <p className="text-xs text-amber-600 mt-1">This program is currently not accepting applications for any terms.</p>
            </div>
          )}
        </div>
      </StepSlide>
    );
  };

  /* ─── Step 3: Requirements ─── */
  const renderStep3 = () => (
    <StepSlide step={3}>
      <div className="space-y-4">
        <div className="flex items-center space-x-2.5 bg-slate-50 rounded-2xl px-4 py-3 border border-slate-200">
          <div className="h-8 w-8 rounded-xl bg-slate-200 flex items-center justify-center shrink-0">
            <GraduationCap className="h-4 w-4 text-slate-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Program</p>
            <p className="text-sm font-bold text-slate-800 truncate">{selectedProgram?.name || '—'}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-200/60 rounded-2xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <Shield className="h-4 w-4 text-amber-700" />
            <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">Program Requirements</p>
          </div>
          {requirementsLoading ? (
            <div className="flex items-center space-x-3 text-slate-500 py-4">
              <Loader className="h-4 w-4 animate-spin" />
              <span className="text-sm font-semibold">Loading requirements...</span>
            </div>
          ) : requirementsText ? (
            <div
              className="text-sm sm:text-base text-slate-700 leading-relaxed prose prose-sm max-w-none [&_a]:text-brand [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h2]:text-sm [&_h2]:font-bold [&_h2]:text-slate-800 [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-xs [&_h3]:font-bold [&_h3]:text-slate-700 [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_li]:mb-0.5"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(marked.parse(requirementsText) as string),
              }}
            />
          ) : (
            <div className="text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-wrap [&_a]:text-brand [&_a]:underline">
              <em className="text-slate-400 text-sm">
                No specific requirements have been set for this program. Please contact
                the administrator if you have questions.
              </em>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-3">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-brand" />
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Confirmation</p>
          </div>
          <label className="flex items-start space-x-3.5 cursor-pointer group py-1">
            <input
              type="checkbox"
              checked={checkboxEligibility}
              onChange={(e) => setCheckboxEligibility(e.target.checked)}
              className="h-5 w-5 mt-0.5 rounded-lg border-2 border-slate-300 text-brand focus:ring-brand-primary cursor-pointer accent-blue-600"
            />
            <span className="text-sm sm:text-base text-slate-700 group-hover:text-slate-900 select-none leading-snug">
              I confirm that I meet the eligibility requirements for this program.
            </span>
          </label>
          <label className="flex items-start space-x-3.5 cursor-pointer group py-1">
            <input
              type="checkbox"
              checked={checkboxReadUnderstand}
              onChange={(e) => setCheckboxReadUnderstand(e.target.checked)}
              className="h-5 w-5 mt-0.5 rounded-lg border-2 border-slate-300 text-brand focus:ring-brand-primary cursor-pointer accent-blue-600"
            />
            <span className="text-sm sm:text-base text-slate-700 group-hover:text-slate-900 select-none leading-snug">
              I have read and understand all program requirements and agree to proceed.
            </span>
          </label>
        </div>
      </div>
    </StepSlide>
  );

  /* ─── Step 4: Resume ─── */
  const renderStep4 = () => (
    <StepSlide step={4}>
      <div className="space-y-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 border border-blue-100 rounded-2xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-start space-x-3">
            <div className="h-11 w-11 rounded-2xl bg-blue-100 flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-brand" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-bold text-blue-900">Jump Start with Your Resume</p>
                <span className="text-[10px] font-extrabold uppercase bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-md">Optional</span>
              </div>
              <p className="text-xs sm:text-sm text-brand mt-0.5 leading-relaxed">
                Upload your resume (PDF or DOCX). We'll try to extract your name and
                contact details so you don't have to type them in.
              </p>
            </div>
          </div>
        </div>

        {!resumeUploaded ? (
          /* §3.4: a clearly BOUNDED upload control — the old version made
             the entire step card one giant <label>, so any stray tap
             opened the file picker and the step read as a single
             ambiguous button. Now the drop zone is delineated, drag &
             drop is explicit, and the actual control is one labelled
             button inside it. */
          <div
            className="border-2 border-dashed border-blue-300 bg-blue-50/30 rounded-2xl px-5 py-5 text-center transition-all duration-200"
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
              const file = e.dataTransfer.files?.[0];
              if (file) void handleResumeUpload(file);
            }}
          >
            <input
              ref={resumeInputRef}
              type="file"
              accept=".pdf,.docx"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleResumeUpload(file);
                e.target.value = '';
              }}
              disabled={isParsing}
              className="hidden"
            />
            <div className="flex flex-col items-center space-y-2.5">
              <div className="h-11 w-11 rounded-2xl bg-blue-100 flex items-center justify-center">
                <Upload className="h-5 w-5 text-brand" />
              </div>
              <p className="text-sm font-bold text-blue-900">Drag your resume here, or</p>
              <button
                type="button"
                onClick={() => resumeInputRef.current?.click()}
                disabled={isParsing}
                className="px-5 py-2.5 btn-primary text-white text-sm font-bold rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isParsing ? 'Parsing…' : 'Choose File'}
              </button>
              <p className="text-xs text-brand">PDF or DOCX, up to 10MB</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-emerald-200 p-4 sm:p-5 shadow-sm space-y-3">
            <div className="flex items-center space-x-2.5 text-emerald-700">
              <div className="h-8 w-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold">Resume {resumeParsed ? 'Uploaded & Parsed' : 'Uploaded'}</p>
                {(resumeFile || resumeFileName) && (
                  <p className="text-xs text-emerald-600 mt-0.5 truncate max-w-[180px] sm:max-w-xs">{resumeFile?.name || resumeFileName}</p>
                )}
              </div>
            </div>
            {resumeParsed && (
              <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Extracted Information</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm text-slate-700">
                  {parsedFirstName && (
                    <div><span className="text-xs font-semibold text-slate-400 block">First Name</span><span>{parsedFirstName}</span></div>
                  )}
                  {parsedLastName && (
                    <div><span className="text-xs font-semibold text-slate-400 block">Last Name</span><span>{parsedLastName}</span></div>
                  )}
                  {parsedEmail && (
                    <div className="col-span-2"><span className="text-xs font-semibold text-slate-400 block">Email</span><span className="truncate block">{parsedEmail}</span></div>
                  )}
                  {parsedPhone && (
                    <div className="col-span-2"><span className="text-xs font-semibold text-slate-400 block">Phone</span><span>{parsedPhone}</span></div>
                  )}
                </div>
              </div>
            )}
            {isParsing && (
              <div className="flex items-center space-x-2.5 text-brand bg-blue-50 rounded-xl px-4 py-3">
                <Loader className="h-4 w-4 animate-spin" />
                <span className="text-sm font-semibold">Parsing your resume...</span>
              </div>
            )}
            {resumeError && (
              <div className="flex items-start space-x-2 text-amber-700 bg-amber-50 rounded-xl px-4 py-3 border border-amber-200">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span className="text-sm">{resumeError}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => { setResumeUploaded(false); setResumeParsed(false); setResumeFile(null); setResumeFileName(''); setResumeError(''); }}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 underline transition-all cursor-pointer"
            >
              Upload a different file
            </button>
          </div>
        )}

        {!resumeUploaded && (
          <button
            type="button"
            onClick={handleSkipResume}
            className="w-full py-3.5 text-sm font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-300 transition-all cursor-pointer active:scale-[0.99]"
          >
            Continue Without a Resume
          </button>
        )}
      </div>
    </StepSlide>
  );

  const activeStepObj = stepList[currentStep - 1];
  const activeStepId = activeStepObj?.id;

  const renderStepContent = () => {
    switch (activeStepId) {
      case 'program': return renderStep1();
      case 'term': return renderStep2();
      case 'variable': return renderVariableStep();
      case 'requirements': return renderStep3();
      case 'resume': return renderStep4();
      default: return null;
    }
  };

  const canGoNext = () => {
    switch (activeStepId) {
      case 'program': return canProceedFromStep1;
      case 'term': return canProceedFromStep2;
      case 'variable': return selectedPricingVariableId !== '';
      case 'requirements': return canProceedFromStep3;
      case 'resume': return canProceedFromStep4;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < stepList.length) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const isLastStep = currentStep === stepList.length;

  return createPortal(
    <>
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      style={{ animation: 'wizFadeIn 0.2s ease-out', zIndex }}
    >
      <div
        className="bg-white w-full sm:rounded-3xl shadow-2xl sm:max-w-lg max-h-[96dvh] sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: 'wizSlideUp 0.3s ease-out',
          borderRadius: '1.5rem 1.5rem 0 0',
        }}
      >
        {/* ── Header ── */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 px-4 sm:px-6 pt-4 sm:pt-5 pb-0 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-3">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <GraduationCap className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold text-white">
                  {mode === 'admin' ? 'New Applicant' : 'New Application'}
                </h3>
                <p className="text-[10px] sm:text-xs text-blue-200 mt-0.5">
                  Step {currentStep} of {stepList.length}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-white/70 hover:text-white rounded-xl hover:bg-white/10 transition-all cursor-pointer active:scale-90"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {renderProgress()}
        </div>

        {/* ── Scrollable Body ── */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5">
          {isOffline && (
            <div className="mb-4 bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start space-x-3 shadow-sm animate-pulse">
              <WifiOff className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-bold text-rose-900">You are currently offline</p>
                <p className="text-[11px] text-rose-700 mt-0.5">Your internet connection was lost. Progress will not be saved. Please reconnect before continuing.</p>
              </div>
            </div>
          )}
          {renderStepContent()}
        </div>

        {/* ── Footer Navigation ── */}
        <div className="shrink-0 border-t border-slate-100 bg-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 shadow-[0_-2px_10px_rgba(0,0,0,0.03)]">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center justify-center space-x-1.5 px-4 py-3 sm:py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 disabled:opacity-20 disabled:cursor-not-allowed transition-all cursor-pointer active:scale-95 rounded-xl hover:bg-slate-50 min-w-[80px]"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back</span>
          </button>

          {isLastStep ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canGoNext() || submitting}
              className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 sm:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-300 text-white text-sm font-bold rounded-2xl transition-all cursor-pointer disabled:cursor-not-allowed active:scale-[0.97] shadow-lg shadow-blue-500/20"
            >
              {submitting ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <span>{mode === 'admin' ? 'Create Applicant' : 'Start Application'}</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext()}
              className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 sm:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-300 text-white text-sm font-bold rounded-2xl transition-all cursor-pointer disabled:cursor-not-allowed active:scale-[0.97] shadow-lg shadow-blue-500/20"
            >
              <span>Continue</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
    <HelpInfoModal
      isOpen={!!wizardHelpModal}
      onClose={() => setWizardHelpModal(null)}
      title={wizardHelpModal?.title || ''}
      explanation={wizardHelpModal?.explanation || ''}
      isSensitivePii={wizardHelpModal?.isSensitive}
    />
    </>,
    document.body
  );
}
